import React from "react";
import styled from "styled-components";

interface AuthLayoutProps {
  cardTitle: string;
  cardSubtitle: string;
  children: React.ReactNode;
}

const Shell = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem 1.25rem 1.75rem;
  background:
    radial-gradient(circle at 16% 18%, rgba(71, 85, 105, 0.18), transparent 45%),
    radial-gradient(circle at 74% 44%, rgba(148, 163, 184, 0.16), transparent 46%),
    radial-gradient(circle at 84% 82%, rgba(51, 65, 85, 0.14), transparent 42%),
    linear-gradient(140deg, #0f172a 0%, #111827 42%, #1f2937 100%);
`;

const Frame = styled.div`
  width: min(1060px, 100%);
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(360px, 460px);
  gap: 2rem;
  position: relative;
  transform: translateY(-4vh);

  &::after {
    content: "";
    position: absolute;
    right: -36px;
    top: 50%;
    transform: translateY(-50%);
    width: 430px;
    height: 430px;
    border-radius: 999px;
    background: radial-gradient(circle, rgba(148, 163, 184, 0.18) 0%, rgba(148, 163, 184, 0) 70%);
    pointer-events: none;
    z-index: 0;
  }

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    transform: translateY(0);

    &::after {
      display: none;
    }
  }
`;

const BrandPanel = styled.aside`
  border-radius: 18px;
  padding: 1.6rem 1.55rem;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.58) 0%, rgba(30, 41, 59, 0.4) 100%);
  box-shadow: 0 12px 32px rgba(2, 6, 23, 0.22);
  backdrop-filter: blur(2px);
  color: #e2e8f0;
  position: relative;
  z-index: 1;

  @media (max-width: 980px) {
    display: none;
  }
`;

const BrandTop = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1.15rem;
`;

const BrandLogo = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 6px;
`;

const BrandName = styled.div`
  font-size: 0.94rem;
  font-weight: 600;
  color: #f8fafc;
`;

const BrandHeadline = styled.h1`
  margin: 0;
  color: #f8fafc;
  font-size: 1.48rem;
  line-height: 1.25;
  letter-spacing: -0.015em;
`;

const BrandSubline = styled.p`
  margin: 0.5rem 0 0;
  color: #cbd5e1;
  font-size: 0.9rem;
  font-weight: 500;
`;

const BrandDescription = styled.p`
  margin: 0.9rem 0 1.35rem;
  color: #94a3b8;
  font-size: 0.92rem;
  line-height: 1.6;
  max-width: 33ch;
`;

const ValueList = styled.div`
  display: grid;
  gap: 0.58rem;
`;

const ValueRow = styled.div`
  font-size: 0.84rem;
  color: #cbd5e1;
`;

const ValueLabel = styled.span`
  color: #94a3b8;
  margin-right: 0.42rem;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
`;

const Card = styled.section`
  border: 1px solid #dbe3ee;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.2), 0 3px 8px rgba(15, 23, 42, 0.1);
  padding: 1.45rem 1.5rem 1.4rem;
  position: relative;
  z-index: 1;
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  color: #111827;
  font-weight: 600;
`;

const CardSubtitle = styled.p`
  margin: 0.42rem 0 1.05rem;
  color: #64748b;
  font-size: 0.86rem;
  line-height: 1.45;
`;

const AuthLayout: React.FC<AuthLayoutProps> = ({ cardTitle, cardSubtitle, children }) => {
  return (
    <Shell>
      <Frame>
        <BrandPanel>
          <BrandTop>
            <BrandLogo src="/logo.png" alt="MedhaCode" />
            <BrandName>MedhaCode</BrandName>
          </BrandTop>

          <BrandHeadline>Precision Before Payment.</BrandHeadline>
          <BrandSubline>Confidence in compliance. Strength in cash flow.</BrandSubline>
          <BrandDescription>
            AI-powered validation for hospital revenue operations. Identify risk, enforce compliance, and strengthen claim confidence before submission.
          </BrandDescription>

          <ValueList>
            <ValueRow>
              <ValueLabel>Focus</ValueLabel>
              Prevent denials before they occur.
            </ValueRow>
            <ValueRow>
              <ValueLabel>Security</ValueLabel>
              Session controls and multi-factor authentication
            </ValueRow>
            <ValueRow>
              <ValueLabel>Outcome</ValueLabel>
              Faster reimbursement with reduced risk exposure.
            </ValueRow>
          </ValueList>
        </BrandPanel>

        <Card>
          <CardTitle>{cardTitle}</CardTitle>
          <CardSubtitle>{cardSubtitle}</CardSubtitle>
          {children}
        </Card>
      </Frame>
    </Shell>
  );
};

export default AuthLayout;
